// Admin Settings Page — Bank info, QR code, and user role management
// Now uses Firestore for persistent storage
import { renderHeader, renderBottomNav } from '../components/header.js';
import { requireAdmin, getCurrentUser, setCurrentUser } from '../auth.js';
import { toastSuccess, toastError } from '../components/toast.js';
import { getAllUsers, updateUserRoleDB, getAdminSettingsDB, saveAdminSettingsDB, uploadFileDB } from '../services/db.js';

/// Default pricing tiers — admin can add/edit/delete from settings
const DEFAULT_PRICING_TIERS = [
  { id: 'has_time', label: 'มีเวลาเกิด (เช่น 8.45 น.)', price: 599, color: '#06C755' },
  { id: 'no_time', label: 'ไม่มีเวลาเกิด / ไม่มั่นใจนาที', price: 999, color: '#f2994a' },
  { id: 'twin', label: 'มีฝาแฝด', price: 999, color: '#f2994a', unit: '/คน' },
  { id: 'abroad_has_time', label: 'เกิดต่างประเทศ มีเวลาเกิด', price: 999, color: '#9b59f0' },
  { id: 'abroad_no_time', label: 'เกิดต่างประเทศ ไม่มีเวลาเกิด', price: 1599, color: '#e94d9e' }
];

// Default settings
const DEFAULT_SETTINGS = {
  bank_name: '',
  account_name: '',
  account_number: '',
  promptpay_id: '',
  qr_code_url: '',
  inter_bank_name: 'Krung Thai Bank',
  inter_swift: 'KRTHTHBK',
  inter_branch: '521',
  inter_account_name: 'Ms. Sassihorn Pattaragrida',
  inter_account_number: '521-050-9974',
  service_note: 'กรุณาโอนเงินและส่งสลิปทาง LINE Chat',
  google_calendar_webhook: '',
  pricing_tiers: DEFAULT_PRICING_TIERS,
  template_new_booking: '🎉 ขอบคุณที่จองคิวดูดวงค่ะ!\n\nคุณ: {{customer_name}}\nนัดหมาย: {{date_time}}\nรายละเอียดบริการ: {{service_name}}\nยอดชำระ: {{price}} บาท\n\n🏦 ชำระในประเทศ:\nธนาคาร: {{bank_name}}\nชื่อบัญชี: {{account_name}}\nเลขบัญชี: {{account_number}}\nพร้อมเพย์: {{promptpay}}\n\n🌍 ชำระต่างประเทศ (International):\nBank: {{inter_bank_name}}\nSWIFT: {{inter_swift}}\nBranch: {{inter_branch}}\nName: {{inter_account_name}}\nAccount: {{inter_account_number}}\n\n📋 เมื่อชำระเงินส่งสลิปแล้ว กรุณากรอกข้อมูลดวงชะตาของท่านที่ลิงก์นี้:\n{{form_link}}\n\n📌 กรุณาโอนเงินและ "ส่งภาพสลิปยืนยัน" เข้ามาในแชทนี้ให้แอดมินด้วยนะคะ',
  template_admin_alert: '🔔 [แอดมิน] มีรายการจองใหม่!\n\nลูกค้า: {{customer_name}}\nนัดหมาย: {{date_time}}\nยอดโอน: {{price}} บาท\n\nโปรดรอตรวจสอบสลิปในแชทและกดยืนยันในหน้า Admin Dashboard',
  template_confirmed: '✅ ยืนยันการจองและได้รับยอดชำระเรียบร้อยค่ะ!\nคุณ: {{customer_name}}\nวันนัดหมาย: {{date_time}}\n\nรบกวนคลิกลิงก์ด้านล่างเพื่อ "บันทึกข้อมูลดวงชะตา" ให้ครบถ้วนนะคะ 👇\n{{form_link}}',
  template_reminder: '⏰ [แจ้งเตือนคิวดูดวง]\nคุณ {{customer_name}} มีคิวดูดวงกับอาจารย์ในอีก 3 ชั่วโมงนะคะ\n(นัดหมาย: {{date_time}})',
  enable_review: true,
  line_oa_bot_id: 'U653089ad031d2896c6431fcb359e0c64'
};

export async function renderAdminSettings() {
  const allowed = await requireAdmin();
  if (!allowed) return;

  const app = document.getElementById('app');
  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <div style="text-align:center; padding: var(--space-xl);">
        <div class="loading-spinner"></div>
        <p class="text-muted mt-md">กำลังโหลด...</p>
      </div>
    </div>
    ${renderBottomNav('admin-settings')}
  `;

  // Load data from Firestore
  const [settings, users] = await Promise.all([
    getAdminSettingsDB(),
    getAllUsers()
  ]);

  const s = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  
  // Auto-upgrade old template if it lacks bank variables
  if (s.template_new_booking && !s.template_new_booking.includes('{{bank_name}}')) {
      s.template_new_booking = DEFAULT_SETTINGS.template_new_booking;
  }

  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <h2 class="page-title">⚙️ ตั้งค่าระบบ</h2>

      <!-- Admin Quick Menu -->
      <div class="card mb-lg" style="background: linear-gradient(135deg, rgba(155,89,240,0.15), rgba(233,77,158,0.1));">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">📌 เมนูจัดการ</h3>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap: var(--space-sm);">
          <button class="btn btn-outline btn-block" onclick="location.hash='#/admin/fortune-tellers'" style="padding:var(--space-md); text-align:left;">
            <span style="font-size:1.2rem;">🔮</span><br/>
            <span class="fw-600">จัดการหมอดู</span><br/>
            <span class="text-xs text-muted">เพิ่ม/แก้ไข/ลบ</span>
          </button>
          <button class="btn btn-outline btn-block" onclick="location.hash='#/admin/schedule'" style="padding:var(--space-md); text-align:left;">
            <span style="font-size:1.2rem;">📅</span><br/>
            <span class="fw-600">ตารางเวลา</span><br/>
            <span class="text-xs text-muted">เปิด/ปิดวัน</span>
          </button>
          <button class="btn btn-outline btn-block" onclick="location.hash='#/admin/bookings'" style="padding:var(--space-md); text-align:left;">
            <span style="font-size:1.2rem;">📋</span><br/>
            <span class="fw-600">การจอง</span><br/>
            <span class="text-xs text-muted">อนุมัติ/ปฏิเสธ</span>
          </button>
          <button class="btn btn-outline btn-block" onclick="location.hash='#/admin/reports'" style="padding:var(--space-md); text-align:left;">
            <span style="font-size:1.2rem;">📊</span><br/>
            <span class="fw-600">รายงาน</span><br/>
            <span class="text-xs text-muted">สถิติ/รายได้</span>
          </button>
        </div>
      </div>

      <!-- 🌸 Pricing Tier Management -->
      <div class="card mb-lg">
        <div class="flex-between mb-md">
          <h3 style="color: var(--accent-gold); margin:0;">🌸 จัดการอัตราค่าครู</h3>
          <button class="btn btn-sm btn-gold" id="add-tier-btn">+ เพิ่ม</button>
        </div>
        <p class="text-xs text-muted mb-md">กำหนดอัตราค่าครูตามเงื่อนไขเวลาเกิด ลูกค้าจะเลือกตอนกรอกข้อมูลดวงชะตา ค่าครูจะอัปเดตตามอัตโนมัติ</p>
        
        <!-- Add/Edit Form (hidden by default) -->
        <div id="tier-form" class="hidden" style="padding:var(--space-md); background:rgba(155,89,240,0.08); border-radius:var(--radius-md); margin-bottom:var(--space-md); border:1px solid rgba(155,89,240,0.2);">
          <h4 id="tier-form-title" style="color:var(--accent-gold); margin-bottom:var(--space-sm);">➕ เพิ่มอัตราค่าครูใหม่</h4>
          <div class="form-group">
            <label class="form-label">ชื่อ/เงื่อนไข *</label>
            <input class="form-input" type="text" id="tier-label" placeholder="เช่น มีเวลาเกิด, มีฝาแฝด" />
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:var(--space-sm);">
            <div class="form-group">
              <label class="form-label">ราคา (บาท) *</label>
              <input class="form-input" type="number" id="tier-price" placeholder="599" />
            </div>
            <div class="form-group">
              <label class="form-label">สี</label>
              <input class="form-input" type="color" id="tier-color" value="#06C755" style="height:40px;" />
            </div>
            <div class="form-group">
              <label class="form-label">หน่วย</label>
              <input class="form-input" type="text" id="tier-unit" placeholder="เช่น /คน" />
            </div>
          </div>
          <div class="flex gap-sm">
            <button class="btn btn-outline btn-block" id="cancel-tier-btn">ยกเลิก</button>
            <button class="btn btn-primary btn-block" id="save-tier-btn">💾 บันทึก</button>
          </div>
        </div>

        <!-- Tier List -->
        <div id="tier-list">
          ${(s.pricing_tiers || DEFAULT_PRICING_TIERS).map((tier, idx) => `
          <div class="card mb-xs" style="padding:var(--space-sm) var(--space-md); background:${tier.color}12; border:1px solid ${tier.color}40;">
            <div style="display:flex; justify-content:space-between; align-items:center; gap:var(--space-sm);">
              <div style="flex:1; min-width:0;">
                <span style="font-weight:600; color:var(--text-primary); font-size:var(--font-sm);">🌸 ${tier.label}</span><br>
                <span style="color:${tier.color}; font-weight:700; font-size:var(--font-md);">฿${tier.price.toLocaleString()}${tier.unit || ''}</span>
              </div>
              <div class="flex gap-xs" style="flex-shrink:0;">
                <button class="btn btn-sm btn-outline edit-tier-btn" data-idx="${idx}" style="padding:4px 8px;">✏️</button>
                <button class="btn btn-sm btn-danger delete-tier-btn" data-idx="${idx}" style="padding:4px 8px;">🗑️</button>
              </div>
            </div>
          </div>
          `).join('')}
        </div>
      </div>

      <!-- Bank Info -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">🏦 ข้อมูลบัญชีธนาคาร</h3>
        <p class="text-xs text-muted mb-md">ข้อมูลนี้จะถูกส่งให้ลูกค้าหลังจองคิว</p>
        
        <div class="form-group">
          <label class="form-label">ธนาคาร</label>
          <input class="form-input" type="text" id="set-bank-name" value="${s.bank_name}" placeholder="เช่น กสิกรไทย, ไทยพาณิชย์" />
        </div>
        <div class="form-group">
          <label class="form-label">ชื่อบัญชี</label>
          <input class="form-input" type="text" id="set-account-name" value="${s.account_name}" placeholder="ชื่อบัญชีธนาคาร" />
        </div>
        <div class="form-group">
          <label class="form-label">เลขบัญชี</label>
          <input class="form-input" type="text" id="set-account-number" value="${s.account_number}" placeholder="XXX-X-XXXXX-X" />
        </div>
        <div class="form-group">
          <label class="form-label">หมายเลขพร้อมเพย์</label>
          <input class="form-input" type="text" id="set-promptpay" value="${s.promptpay_id || ''}" placeholder="เบอร์โทรหรือเลขบัตรประชาชน" />
        </div>
        
        <h3 style="color: var(--accent-gold); margin-top: var(--space-lg); margin-bottom: var(--space-md);">🌍 บัญชีโอนเงินต่างประเทศ (International)</h3>
        <div class="form-group">
          <label class="form-label">Bank Name</label>
          <input class="form-input" type="text" id="set-inter-bank" value="${s.inter_bank_name}" placeholder="Krung Thai Bank" />
        </div>
        <div class="form-group">
          <label class="form-label">SWIFT Code</label>
          <input class="form-input" type="text" id="set-inter-swift" value="${s.inter_swift}" placeholder="KRTHTHBK" />
        </div>
        <div class="form-group">
          <label class="form-label">Branch Code</label>
          <input class="form-input" type="text" id="set-inter-branch" value="${s.inter_branch}" placeholder="521" />
        </div>
        <div class="form-group">
          <label class="form-label">Account Name</label>
          <input class="form-input" type="text" id="set-inter-account-name" value="${s.inter_account_name}" placeholder="Ms. Name Lastname" />
        </div>
        <div class="form-group">
          <label class="form-label">Account Number</label>
          <input class="form-input" type="text" id="set-inter-account-number" value="${s.inter_account_number}" placeholder="XXX-XXX-XXXX" />
        </div>

        <div class="form-group" style="margin-top:var(--space-md);">
          <label class="form-label">หมายเหตุ/ข้อความเพิ่มเติม</label>
          <textarea class="form-textarea" id="set-note" rows="2" placeholder="ข้อความที่จะส่งให้ลูกค้า">${s.service_note || ''}</textarea>
        </div>
        <button class="btn btn-primary btn-block" id="save-bank-btn">💾 บันทึกข้อมูลบัญชี</button>
      </div>

      <!-- Integrations / Google Calendar & LINE OA -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">🔧 การเชื่อมต่อภายนอก (Integrations)</h3>
        
        <div class="form-group mb-md">
          <label class="form-label">Google Calendar Webhook URL</label>
          <p class="text-xs text-muted mb-xs">ใส่ Webhook URL (จาก Make.com หรือ Apps Script) เพื่อให้อัปเดตปฏิทินออโต้</p>
          <input class="form-input" type="url" id="set-webhook-url" value="${s.google_calendar_webhook || ''}" placeholder="https://hook.us1.make.com/xxx..." />
        </div>

        <div class="form-group mb-md" style="border-top:1px dashed var(--border); padding-top:var(--space-md);">
          <label class="form-label">LINE OA Provider ID (สำหรับเปิดหน้าแชท)</label>
          <p class="text-xs text-muted mb-xs">รหัสที่ขึ้นต้นด้วย <code>U</code> (ดูจาก URL เวลาเปิดแชทบน LINE OA Manager เช่น <code>https://chat.line.biz/U1234...</code>)</p>
          <input class="form-input" type="text" id="set-line-oa-bot-id" value="${s.line_oa_bot_id || ''}" placeholder="Uxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
        </div>

        <button class="btn btn-primary btn-block" id="save-integrations-btn">💾 บันทึกการเชื่อมต่อ</button>
      </div>

      <!-- PIN Management -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">🔑 รหัสผ่านแอดมิน (PIN)</h3>
        <p class="text-xs text-muted mb-md">ใช้สำหรับล็อกอินเข้าหลังบ้านโดยไม่ผ่าน LINE (ค่าเริ่มต้น: 888888)</p>
        
        <div class="form-group mb-sm">
          <label class="form-label">PIN ใหม่ (ตัวเลข 6 หลัก)</label>
          <input class="form-input" type="password" id="set-admin-pin" value="${s.system_pin || ''}" placeholder="888888" maxlength="6" inputmode="numeric" style="letter-spacing:0.5rem;" />
        </div>
        <button class="btn btn-primary btn-block" id="save-pin-btn">💾 อัปเดต PIN</button>
      </div>

      <!-- QR Code Upload -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">📱 รูปภาพ QR Code พร้อมเพย์</h3>
        <p class="text-xs text-muted mb-md">ใส่ลิงก์รูป QR Code (เช่น จากเว็บฝากรูป) หรืออัปโหลดไฟล์</p>

        <div class="form-group mb-md">
          <label class="form-label">ลิงก์รูปภาพ (Image URL)</label>
          <input class="form-input" type="url" id="set-qr-url-input" value="${s.qr_code_url && s.qr_code_url.startsWith('http') ? s.qr_code_url : ''}" placeholder="https://..." />
        </div>
        
        <div style="text-align:center; font-weight:bold; margin-bottom:var(--space-md);">--- หรือ ---</div>

        ${s.qr_code_url ? `
          <div style="text-align:center; margin-bottom: var(--space-md);">
            <img src="${s.qr_code_url}" alt="QR Code" 
                 style="max-width:180px; border-radius: var(--radius-md); border: 2px solid var(--accent-gold);" />
          </div>
        ` : ''}
        
        <div class="file-upload" id="qr-upload-area">
          <div class="file-upload-icon">📤</div>
          <div class="file-upload-text">อัปโหลดไฟล์รูปใหม่ (ต้องเซ็ต Firebase สำเร็จ)</div>
          <input type="file" id="qr-file-input" accept="image/*" />
        </div>

        <div id="qr-preview" class="hidden">
          <div class="file-preview mt-md">
            <img id="qr-preview-img" src="" alt="QR Preview" />
          </div>
        </div>
        
        <button class="btn btn-gold btn-block mt-md" id="save-qr-btn">💾 บันทึก QR Code</button>
      </div>

      <!-- Message Templates Management -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">💬 ตั้งค่าข้อความแจ้งเตือนอัตโนมัติ</h3>
        <p class="text-xs text-muted mb-md">แก้ไขข้อความที่จะส่งให้ลูกค้าและแอดมินทาง LINE อัตโนมัติ (ใช้ตัวแปร <code>{{customer_name}}</code>, <code>{{date_time}}</code>, <code>{{service_name}}</code>, <code>{{price}}</code>, <code>{{form_link}}</code> เพื่อดึงข้อมูลจริง)</p>
        
        <div class="form-group">
          <label class="form-label" style="color:var(--accent-gold);">1. ข้อความเมื่อลูกค้าจองคิวใหม่ (ส่งหาลูกค้า)</label>
          <textarea class="form-textarea" id="set-tpl-new-booking" rows="5">${s.template_new_booking || ''}</textarea>
        </div>
        
        <div class="form-group">
          <label class="form-label" style="color:var(--accent-gold);">2. ข้อความเตือนมีคนจอง (ส่งหาแอดมิน)</label>
          <textarea class="form-textarea" id="set-tpl-admin-alert" rows="4">${s.template_admin_alert || ''}</textarea>
        </div>

        <div class="form-group">
          <label class="form-label" style="color:#06C755;">3. ข้อความยืนยันคิว (ส่งหาลูกค้าตอนได้รับสลิป)</label>
          <p class="text-xs text-muted mb-sm">*แนะนำ: ควรมีลิงก์ <code>{{form_link}}</code> เพื่อให้ลูกค้าเข้าไปกรอกข้อมูลดวงชะตาและอัพรูปหน้าตรง</p>
          <textarea class="form-textarea" id="set-tpl-confirmed" rows="5">${s.template_confirmed || ''}</textarea>
        </div>

        <div class="form-group">
          <label class="form-label" style="color:#f2994a;">4. แจ้งเตือนล่วงหน้า 3 ชั่วโมง (ส่งหาลูกค้า)</label>
          <textarea class="form-textarea" id="set-tpl-reminder" rows="3">${s.template_reminder || ''}</textarea>
        </div>

        <div class="form-group pt-sm" style="border-top:1px dashed var(--border);">
          <label class="form-label" style="display:flex; align-items:center; gap:8px;">
            <input type="checkbox" id="set-enable-review" ${s.enable_review !== false ? 'checked' : ''} style="width:18px; height:18px; accent-color: var(--accent-gold);" />
            <span>🌟 เปิดระบบขอรีวิวสั้นๆ ตอนดูดวงเสร็จ (ส่งให้ลูกค้า)</span>
          </label>
        </div>

        <button class="btn btn-primary btn-block" id="save-templates-btn">💾 บันทึกข้อความแจ้งเตือน</button>
      </div>

      <!-- User Management -->
      <div class="card">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">👥 จัดการสิทธิ์ผู้ใช้</h3>
        <p class="text-xs text-muted mb-md">กำหนดสิทธิ์ให้ผู้ใช้ที่เคย Login ผ่าน LINE</p>
        
        <div id="user-list">
          ${users.length > 0 ? users.map(u => renderUserRow(u)).join('') : `
            <div class="empty-state">
              <div class="empty-state-icon">👤</div>
              <div class="empty-state-text">ยังไม่มีผู้ใช้</div>
            </div>
          `}
        </div>
      </div>
    </div>
    ${renderBottomNav('admin-settings')}
  `;

  bindEvents(s);
}

function renderUserRow(user) {
  return `
    <div class="card" style="margin-bottom: var(--space-sm); padding: var(--space-md);">
      <div style="display:flex; gap: var(--space-md); align-items:center;">
        <img src="${user.pictureUrl || 'https://ui-avatars.com/api/?name=U&background=9b59f0&color=fff&size=40'}" 
             alt="${user.name}" 
             style="width:40px; height:40px; border-radius:50%; object-fit:cover;" 
             onerror="this.onerror=null; this.src='https://ui-avatars.com/api/?name=U&background=9b59f0&color=fff&size=40'" />
        <div style="flex:1; min-width:0;">
          <p class="fw-600" style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${user.name}</p>
          <p class="text-xs text-muted">${user.id.substring(0, 12)}...</p>
        </div>
        <select class="form-select user-role-select" data-user-id="${user.id}" 
                style="width:auto; min-width:100px; padding: var(--space-xs) var(--space-md); font-size: var(--font-xs);">
          <option value="customer" ${user.role === 'customer' ? 'selected' : ''}>👤 ลูกดวง</option>
          <option value="fortune_teller" ${user.role === 'fortune_teller' ? 'selected' : ''}>🔮 หมอดู</option>
          <option value="admin" ${user.role === 'admin' ? 'selected' : ''}>👑 แอดมิน</option>
        </select>
      </div>
    </div>
  `;
}

function bindEvents(currentSettings) {
  // Current tiers from settings
  const tiers = currentSettings.pricing_tiers || DEFAULT_PRICING_TIERS;
  let editingTierIdx = -1;

  // --- Pricing Tier CRUD ---
  document.getElementById('add-tier-btn')?.addEventListener('click', () => {
    editingTierIdx = -1;
    document.getElementById('tier-form')?.classList.remove('hidden');
    document.getElementById('tier-form-title').textContent = '➕ เพิ่มอัตราค่าครูใหม่';
    document.getElementById('tier-label').value = '';
    document.getElementById('tier-price').value = '';
    document.getElementById('tier-color').value = '#06C755';
    document.getElementById('tier-unit').value = '';
  });

  document.getElementById('cancel-tier-btn')?.addEventListener('click', () => {
    editingTierIdx = -1;
    document.getElementById('tier-form')?.classList.add('hidden');
  });

  document.getElementById('save-tier-btn')?.addEventListener('click', async () => {
    const label = document.getElementById('tier-label')?.value?.trim();
    const price = parseInt(document.getElementById('tier-price')?.value);
    const color = document.getElementById('tier-color')?.value || '#06C755';
    const unit = document.getElementById('tier-unit')?.value?.trim() || '';

    if (!label || isNaN(price) || price <= 0) {
      toastError('กรุณากรอกชื่อและราคาให้ครบถ้วน');
      return;
    }

    const btn = document.getElementById('save-tier-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';

    const updatedTiers = [...tiers];
    const tierData = {
      id: editingTierIdx >= 0 ? tiers[editingTierIdx].id : label.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase() + '_' + Date.now(),
      label,
      price,
      color,
      ...(unit ? { unit } : {})
    };

    if (editingTierIdx >= 0) {
      updatedTiers[editingTierIdx] = tierData;
    } else {
      updatedTiers.push(tierData);
    }

    await saveAdminSettingsDB({ pricing_tiers: updatedTiers });
    toastSuccess(editingTierIdx >= 0 ? 'อัปเดตค่าครูแล้ว ✅' : 'เพิ่มค่าครูใหม่แล้ว ✅');
    editingTierIdx = -1;
    renderAdminSettings();
  });

  // Edit tier buttons
  document.querySelectorAll('.edit-tier-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.idx);
      const tier = tiers[idx];
      if (!tier) return;
      editingTierIdx = idx;
      document.getElementById('tier-form')?.classList.remove('hidden');
      document.getElementById('tier-form-title').textContent = '✏️ แก้ไขอัตราค่าครู';
      document.getElementById('tier-label').value = tier.label || '';
      document.getElementById('tier-price').value = tier.price || '';
      document.getElementById('tier-color').value = tier.color || '#06C755';
      document.getElementById('tier-unit').value = tier.unit || '';
      window.scrollTo({ top: document.getElementById('tier-form').offsetTop - 80, behavior: 'smooth' });
    });
  });

  // Delete tier buttons
  document.querySelectorAll('.delete-tier-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const idx = parseInt(btn.dataset.idx);
      if (!confirm(`ลบอัตราค่าครู "${tiers[idx]?.label}" ?`)) return;
      const updatedTiers = tiers.filter((_, i) => i !== idx);
      await saveAdminSettingsDB({ pricing_tiers: updatedTiers });
      toastSuccess('ลบค่าครูแล้ว');
      renderAdminSettings();
    });
  });

  // Save bank info
  document.getElementById('save-bank-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('save-bank-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';

    await saveAdminSettingsDB({
      bank_name: document.getElementById('set-bank-name')?.value || '',
      account_name: document.getElementById('set-account-name')?.value || '',
      account_number: document.getElementById('set-account-number')?.value || '',
      promptpay_id: document.getElementById('set-promptpay')?.value || '',
      inter_bank_name: document.getElementById('set-inter-bank')?.value || '',
      inter_swift: document.getElementById('set-inter-swift')?.value || '',
      inter_branch: document.getElementById('set-inter-branch')?.value || '',
      inter_account_name: document.getElementById('set-inter-account-name')?.value || '',
      inter_account_number: document.getElementById('set-inter-account-number')?.value || '',
      service_note: document.getElementById('set-note')?.value || ''
    });

    toastSuccess('บันทึกข้อมูลบัญชีแล้ว ✅');
    btn.disabled = false;
    btn.textContent = '💾 บันทึกข้อมูลบัญชี';
  });

  // Save Templates
  document.getElementById('save-templates-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('save-templates-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';

    await saveAdminSettingsDB({
      template_new_booking: document.getElementById('set-tpl-new-booking')?.value || '',
      template_admin_alert: document.getElementById('set-tpl-admin-alert')?.value || '',
      template_confirmed: document.getElementById('set-tpl-confirmed')?.value || '',
      template_reminder: document.getElementById('set-tpl-reminder')?.value || '',
      enable_review: document.getElementById('set-enable-review')?.checked ?? true
    });

    toastSuccess('บันทึกข้อความเทมเพลตแล้ว ✅');
    btn.disabled = false;
    btn.textContent = '💾 บันทึกข้อความแจ้งเตือน';
  });

  // Save integrations info
  document.getElementById('save-integrations-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('save-integrations-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';

    await saveAdminSettingsDB({
      google_calendar_webhook: document.getElementById('set-webhook-url')?.value || '',
      line_oa_bot_id: document.getElementById('set-line-oa-bot-id')?.value || ''
    });

    toastSuccess('บันทึกการเชื่อมต่อแล้ว ✅');
    btn.disabled = false;
    btn.textContent = '💾 บันทึกการเชื่อมต่อ';
  });

  // Save admin PIN
  document.getElementById('save-pin-btn')?.addEventListener('click', async () => {
    const pin = document.getElementById('set-admin-pin')?.value;
    if (pin.length !== 6 || isNaN(pin)) {
      toastError('กรุณากรอก PIN ให้ครบ 6 หลัก และเป็นตัวเลขเท่านั้น');
      return;
    }
    
    const btn = document.getElementById('save-pin-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';
    try {
      await saveAdminSettingsDB({ system_pin: pin });
      toastSuccess('บันทึกรหัส PIN ใหม่เรียบร้อยแล้ว ✅');
    } catch(err) {
      toastError('บันทึกไม่สำเร็จ');
      console.error(err);
    } finally {
      btn.disabled = false;
      btn.textContent = '💾 อัปเดต PIN';
    }
  });

  // QR upload
  const qrUploadArea = document.getElementById('qr-upload-area');
  const qrFileInput = document.getElementById('qr-file-input');

  qrUploadArea?.addEventListener('click', () => qrFileInput?.click());

  qrFileInput?.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      document.getElementById('qr-preview-img').src = ev.target.result;
      document.getElementById('qr-preview').classList.remove('hidden');
    };
    reader.readAsDataURL(file);
  });

  document.getElementById('save-qr-btn')?.addEventListener('click', async () => {
    const file = document.getElementById('qr-file-input').files[0];
    const imgSrc = document.getElementById('qr-preview-img')?.src;
    const urlInput = document.getElementById('set-qr-url-input')?.value;
    
    // If user provided a Direct URL in the text box, prioritize it!
    if (urlInput && urlInput.startsWith('http')) {
        const btn = document.getElementById('save-qr-btn');
        btn.disabled = true;
        btn.textContent = '⏳ กำลังบันทึก...';
        await saveAdminSettingsDB({ qr_code_url: urlInput });
        toastSuccess('บันทึกรูปด้วยลิงก์ URL แล้ว ✅');
        renderAdminSettings();
        return;
    }

    if (imgSrc) {
      const btn = document.getElementById('save-qr-btn');
      btn.disabled = true;
      btn.textContent = '⏳ กำลังอัพโหลดขึ้น Cloud...';

      let finalUrl = imgSrc;
      // If a new file was picked, upload it to Firebase Storage so LINE can access it
      if (file) {
          try {
              const uploadedUrl = await uploadFileDB(file, 'system_images');
              if (uploadedUrl) {
                  finalUrl = uploadedUrl;
              }
          } catch(e) {
              console.error('QR Upload failed:', e);
              toastError('อัปโหลดไฟล์ล้มเหลว (คุณอาจยังไม่ได้ติดตั้ง Storage)');
              btn.disabled = false;
              btn.textContent = '💾 บันทึก QR Code';
              return;
          }
      }

      await saveAdminSettingsDB({ qr_code_url: finalUrl });
      toastSuccess('บันทึก QR Code บนคลาวด์แล้ว ✅');
      renderAdminSettings();
    } else {
        toastError('กรุณาใส่ลิงก์รูป หรืออัปโหลดไฟล์ครับ');
    }
  });

  // User role selects
  const currentUser = getCurrentUser();
  document.querySelectorAll('.user-role-select').forEach(select => {
    select.addEventListener('change', async () => {
      const userId = select.dataset.userId;
      const role = select.value;

      await updateUserRoleDB(userId, role);

      // If changing own role, update current session
      if (currentUser && userId === currentUser.id) {
        currentUser.role = role;
        setCurrentUser(currentUser);
      }

      const label = role === 'fortune_teller' ? 'หมอดู' : role === 'admin' ? 'แอดมิน' : 'ลูกดวง';
      toastSuccess(`เปลี่ยนสิทธิ์เป็น ${label} แล้ว`);
    });
  });
}
